import { useCallback } from 'react';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../../config/firebase';
import logger from '../../../utils/logger';

const DEFAULT_FALLBACK_THUMBNAIL =
  'https://gs-7b1e1.web.app/assets/default-thumbnail.jpg';

/**
 * Resolves the highest-quality thumbnail URL we have in hand. Tries
 * `maxres → standard → high → medium → default` on the fetched
 * thumbnail map, then falls back to the known-safe default. This is
 * called once during submit, not per render.
 */
const pickThumbnailUrl = (fetchedThumbnail) =>
  fetchedThumbnail?.maxres?.url ||
  fetchedThumbnail?.standard?.url ||
  fetchedThumbnail?.high?.url ||
  fetchedThumbnail?.medium?.url ||
  fetchedThumbnail?.default?.url ||
  DEFAULT_FALLBACK_THUMBNAIL;

/**
 * Encapsulates the submit flow for both create and edit paths.
 *
 *   - Runs full-form validation (via the validation hook's `validateAll`)
 *     and surfaces errors through `showAlert` before anything hits the
 *     network.
 *   - Gates on URL + thumbnail + songName presence with user-facing
 *     alerts (these are legacy defensive checks that also cover the
 *     edge case where validation passes but required derived data is
 *     missing, e.g. thumbnail still loading).
 *   - Shows the posting overlay only after all gates pass (so a failed
 *     validation does not flash the loading screen).
 *   - Build payload, then branches:
 *       * edit: optimistic `setSongs` + `updateDoc` + `mentionNotification`
 *         (with previous-text / previous-handles diff for the server).
 *       * create: optimistic `setSongs(prepend)` + `postSong` callable +
 *         `mentionNotification`. Uses local+canonical dates that match
 *         the server's timezone logic so the optimistic entry slots into
 *         the right day-group instantly.
 *   - On success, calls the provided `onResetForm` so the parent clears
 *     the appropriate state slices, then invokes the parent's
 *     `onSuccess` callback.
 */
const useSubmitSong = ({
  editorRef,
  songName,
  songArtist,
  songURL,
  selectedGenres,
  fetchedThumbnail,
  isEditing,
  postToEdit,
  songs,
  setSongs,
  validateAll,
  sanitizers,
  showAlert,
  onSuccess,
  onPostingChange,
  onResetForm,
  isLoading,
  setIsLoading,
}) => {
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (isLoading) return;
      setIsLoading(true);

      if (!auth.currentUser) {
        showAlert('Login Required', 'Please log in to submit a song!');
        setIsLoading(false);
        return;
      }

      const sanitizedSongName = sanitizers.text(songName);
      const sanitizedSongArtist = sanitizers.text(songArtist);
      const rawDescription = editorRef.current?.getHTML() ?? '';
      const sanitizedDescription = sanitizers.html(rawDescription);
      const sanitizedGenres = selectedGenres
        .map((genre) => sanitizers.text(genre))
        .filter(Boolean);

      logger.debug('PostForm submit validation payload', {
        songName: sanitizedSongName,
        songNameLength: sanitizedSongName.length,
        songArtist: sanitizedSongArtist,
        songArtistLength: sanitizedSongArtist.length,
        descriptionCharCount: editorRef.current?.getCharacterCount() ?? 0,
        genres: sanitizedGenres,
      });

      const formData = {
        songName: sanitizedSongName,
        songArtist: sanitizedSongArtist,
        URL: songURL,
        genres: sanitizedGenres,
      };

      const { ok } = validateAll(formData);
      if (!ok) {
        showAlert(
          'Validation Error',
          'Please fix the validation errors before submitting.'
        );
        setIsLoading(false);
        return;
      }

      if (!songURL || !songURL.trim()) {
        showAlert('Missing URL', 'Please provide a YouTube URL for the song!');
        setIsLoading(false);
        return;
      }

      if (!fetchedThumbnail) {
        showAlert(
          'Loading Thumbnail',
          'Please wait for the thumbnail to load before submitting!'
        );
        setIsLoading(false);
        return;
      }

      const hasValidThumbnail =
        fetchedThumbnail.default?.url ||
        (isEditing && postToEdit?.thumbnailURL);

      if (!hasValidThumbnail) {
        showAlert(
          'Loading Thumbnail',
          'Please wait for the thumbnail to load before submitting!'
        );
        setIsLoading(false);
        return;
      }

      if (!songName || !songName.trim()) {
        showAlert('Missing Song Name', 'Please provide a song name!');
        setIsLoading(false);
        return;
      }

      // Only show the posting overlay once validation has passed — otherwise
      // a failed check flashes the "Warming up the subs…" screen.
      onPostingChange(true, {
        thumbnailURL: pickThumbnailUrl(fetchedThumbnail),
        songName,
        songArtist,
      });

      try {
        const userId = auth.currentUser.uid;
        const timezoneOffset = new Date().getTimezoneOffset();

        const baseSongData = {
          userId,
          songName: sanitizedSongName,
          songArtist: sanitizedSongArtist,
          URL: songURL,
          description: sanitizedDescription,
          mentionedHandles: editorRef.current?.getMentionedHandles() ?? [],
          genres: sanitizedGenres,
          thumbnailURL: pickThumbnailUrl(fetchedThumbnail),
        };

        const creationPayload = {
          ...baseSongData,
          timezoneOffset,
        };

        if (isEditing && postToEdit) {
          try {
            const updatePayload = { ...baseSongData };

            const updatedSongs = songs.map((song) =>
              song.id === postToEdit.id
                ? { ...song, ...updatePayload }
                : song
            );
            setSongs(updatedSongs);

            const songRef = doc(getFirestore(), 'songs', postToEdit.id);
            await updateDoc(songRef, updatePayload);

            const mentionedHandlesForNotif =
              editorRef.current?.getMentionedHandles() ?? [];
            httpsCallable(
              functions,
              'mentionNotification'
            )({
              text: sanitizedDescription,
              mentionedHandles: mentionedHandlesForNotif,
              songId: postToEdit.id,
              mentionContext: 'post',
              previousText: postToEdit.description ?? '',
              previousMentionedHandles: postToEdit.mentionedHandles ?? [],
            }).catch((err) =>
              logger.error('Error sending mention notification:', err)
            );
          } catch (error) {
            logger.error('Error updating song:', error);
            setSongs(songs);
            throw error;
          }
        } else {
          try {
            const postSong = httpsCallable(functions, 'postSong');
            logger.debug('Sending song data');
            const result = await postSong(creationPayload);
            logger.debug('Post song result received');
            if (!result.data.success) {
              // Server explicitly rejected the request (daily limit, validation).
              // Show the message directly — this is not an unexpected error.
              showAlert('Cannot Post', result.data.message);
              return;
            }

            const newSongId = result.data.songId;
            // Picker inserts mention nodes; typed @handle is plain text only
            // — server parses @ from HTML when mentionedHandles is empty.
            if (newSongId && sanitizedDescription.trim()) {
              const mentionedHandles =
                editorRef.current?.getMentionedHandles() ?? [];
              httpsCallable(
                functions,
                'mentionNotification'
              )({
                text: sanitizedDescription,
                mentionedHandles,
                songId: newSongId,
                mentionContext: 'post',
              }).catch((err) =>
                logger.error('Error sending mention notification:', err)
              );
            }

            // Optimistic insert uses the same timezone math as the server
            // so the song slots into the correct day-group immediately.
            const userLocalDate = new Date(
              Date.now() - timezoneOffset * 60 * 1000
            );
            const todayDate = userLocalDate.toISOString().split('T')[0];
            const canonicalDate = new Date().toISOString().split('T')[0];

            const newSong = {
              id: newSongId || `temp-${Date.now()}`,
              ...baseSongData,
              timestamp: { toDate: () => new Date() },
              date: todayDate,
              posterLocalDate: todayDate,
              canonicalDate,
              isFirstPost: result.data.isFirstPost === true,
            };
            setSongs([newSong, ...songs]);
          } catch (error) {
            setSongs(songs);
            throw error;
          }
        }

        onSuccess();
        onResetForm();
      } catch (error) {
        logger.error('Error in handleSubmit:', error);
        showAlert('Submission Error', 'There was an error posting this song...');
      } finally {
        setIsLoading(false);
        onPostingChange(false);
      }
    },
    [
      editorRef,
      songName,
      songArtist,
      songURL,
      selectedGenres,
      fetchedThumbnail,
      isEditing,
      postToEdit,
      songs,
      setSongs,
      validateAll,
      sanitizers,
      showAlert,
      onSuccess,
      onPostingChange,
      onResetForm,
      isLoading,
      setIsLoading,
    ]
  );

  return { handleSubmit };
};

export default useSubmitSong;
